import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import type { SaveConfirmationDialogProps } from '../../../../types/saveConfirmationDialogTypes';
import {
  buildInvalidGameText,
  buildInvalidPackText,
  buildSaveConfirmationCopy,
  getSaveConfirmationButtonsDisabled,
  invalidGamesAlertConfig,
  invalidPacksAlertConfig,
} from '../utils/saveConfirmationDialogUtils';

const SaveConfirmationDialog: React.FC<SaveConfirmationDialogProps> = React.memo(({
  open,
  onCancel,
  onConfirm,
  saving,
  invalidGames,
  invalidPacks,
  gamesData = [],
  validating = false,
  simulationLoading = false,
  simulationError = null,
  simulationResult = null,
  action = 'finalize',
}) => {
  const [countdown, setCountdown] = useState(3);
  const hasInvalidGames = invalidGames.length > 0;
  const hasInvalidPacks = invalidPacks.length > 0;
  const hasErrors = hasInvalidGames || hasInvalidPacks;
  const isRejectAction = action === 'reject';
  const copy = buildSaveConfirmationCopy(action, hasErrors);
  const buttonsDisabled = getSaveConfirmationButtonsDisabled({
    countdown,
    isRejectAction,
    saving,
    validating,
  });

  useEffect(() => {
    if (!open) {
      setCountdown(3);
      return;
    }

    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((previousValue) => {
        if (previousValue <= 1) {
          clearInterval(interval);
          return 0;
        }

        return previousValue - 1;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [open]);

  return (
    <Dialog open={open} onClose={buttonsDisabled ? undefined : onCancel} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          background: '#EEE8FF',
          color: '#222B45',
          fontWeight: 'bold',
          mb: 0
        }}
      >
        {copy.title}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {copy.heading}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy.description}
          </Typography>
        </Box>

        {validating && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Validating games and packs...
          </Alert>
        )}

        {countdown > 0 && !validating && !isRejectAction && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Review the data carefully. Actions will be enabled in {countdown} second
            {countdown !== 1 ? 's' : ''}.
          </Alert>
        )}

        {hasInvalidGames && (
          <Alert severity={invalidGamesAlertConfig.severity} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {invalidGamesAlertConfig.title} ({invalidGames.length})
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {invalidGamesAlertConfig.description}
            </Typography>
            <List dense>
              {invalidGames.map((item, index) => (
                <ListItem key={`invalid-game-${item.game}-${index}`} sx={{ pl: 0 }}>
                  <ListItemText
                    primary={buildInvalidGameText(item)}
                    secondary="This game is not active for this store"
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {hasInvalidPacks && (
          <Alert severity={invalidPacksAlertConfig.severity} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {invalidPacksAlertConfig.title} ({invalidPacks.length})
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {invalidPacksAlertConfig.description}
            </Typography>
            <List dense>
              {invalidPacks.map((item, index) => (
                <ListItem key={`invalid-pack-${item.game}-${item.pack}-${index}`} sx={{ pl: 0 }}>
                  <ListItemText
                    primary={buildInvalidPackText(item)}
                    secondary={`Pack ${item.pack} is not active for Game ${item.game}`}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {!isRejectAction && (
          <Box sx={{ mt: 2 }}>
            {simulationLoading && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Running audit simulation preview...
              </Alert>
            )}

            {simulationError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Unable to load simulation preview: {simulationError}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onCancel}
          disabled={buttonsDisabled}
          variant="outlined"
          sx={{ color: '#2C6CA0', borderColor: '#2C6CA0', fontWeight: 600, fontSize: '15px' }}
        >
          Go Back and Review
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={isRejectAction || hasErrors ? 'error' : 'primary'}
          disabled={buttonsDisabled}
          sx={{
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '15px',
            backgroundColor: isRejectAction || hasErrors ? '#FF0000' : '#2C6CA0',
          }}
        >
          {saving ? 'Saving...' : copy.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

SaveConfirmationDialog.displayName = 'SaveConfirmationDialog';

export default SaveConfirmationDialog;
